import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/auth/AuthContext";
import { createUser, fetchUsers, setUserActive } from "@/api/users";
import type { User } from "@/types";

function errorMessage(e: unknown): string {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Something went wrong.";
}

export function UsersPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const users = useQuery({ queryKey: ["users"], queryFn: fetchUsers });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const add = useMutation({
    mutationFn: () => createUser({ email: email.trim(), name: name.trim() || undefined }),
    onSuccess: () => {
      setEmail("");
      setName("");
      invalidate();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => setUserActive(id, active),
    onSuccess: invalidate,
  });

  // Super-admins only — the API enforces this too, but don't even render for others.
  if (user && !user.super_admin) return <Navigate to="/" replace />;

  return (
    <AppLayout>
      <Typography variant="h5" gutterBottom>Users</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sign-in is invite-only. Add a Freedom Solar email to grant access, then share the app URL — they log in
        with Google and their account binds on first sign-in. Deactivate to revoke access; it signs the person
        out immediately and is reversible.
      </Typography>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); add.mutate(); }} sx={{ mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
          <TextField
            label="Email" type="email" size="small" required value={email}
            onChange={(e) => setEmail(e.target.value)} sx={{ minWidth: 280 }}
          />
          <TextField
            label="Name (optional)" size="small" value={name}
            onChange={(e) => setName(e.target.value)} sx={{ minWidth: 200 }}
          />
          <Button type="submit" variant="contained" disabled={add.isPending || email.trim() === ""}>
            {add.isPending ? "Adding…" : "Add user"}
          </Button>
        </Stack>
        {add.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(add.error)}</Alert>}
      </Box>

      {toggle.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(toggle.error)}</Alert>}

      {users.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.data?.map((u: User) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.name ?? "—"}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Chip size="small" label={u.active ? "Active" : "Inactive"} color={u.active ? "success" : "default"} />
                    {u.super_admin && <Chip size="small" label="Admin" color="info" variant="outlined" />}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {u.active ? (
                    <Button
                      size="small" color="error"
                      disabled={toggle.isPending || u.super_admin || u.id === user?.id}
                      onClick={() => toggle.mutate({ id: u.id, active: false })}
                    >
                      Deactivate
                    </Button>
                  ) : (
                    <Button
                      size="small" disabled={toggle.isPending}
                      onClick={() => toggle.mutate({ id: u.id, active: true })}
                    >
                      Reactivate
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.data?.length === 0 && (
              <TableRow><TableCell colSpan={4}><em>No users yet.</em></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </AppLayout>
  );
}
